import { CaptureStepTypes } from "@enums/capture-step-types";
import { CaptureStep } from "@idw-types/data/capture-step";
import { DriverLicenseSide } from "@enums/driver-license-side";
import { IDCardSide } from "@enums/id-card-side";
import { DCUIContent } from "@iddataweb/idw-common-ui/types";
import { Logger } from "./logger";

export const getCaptureStepStartInstructionsImageUrl = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return (content.face.useCustomStartInstructionImage && content.face.startInstructionsImageUrl)
                ? content.face.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/face.jpg`;
        case CaptureStepTypes.LIVENESS_CHECK:
            return (content.face3d.useCustomStartInstructionImage && content.face3d.startInstructionsImageUrl)
                ? content.face3d.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/face.jpg`;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return (content.dlfront.useCustomStartInstructionImage && content.dlfront.startInstructionsImageUrl)
                ? content.dlfront.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/dlfront.jpg`;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return (content.dlback.useCustomStartInstructionImage && content.dlback.startInstructionsImageUrl)
                ? content.dlback.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/dlback.jpg`;
        case CaptureStepTypes.ID_CARD_FRONT:
            return (content.idfront.useCustomStartInstructionImage && content.idfront.startInstructionsImageUrl)
                ? content.idfront.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/idfront.jpg`;
        case CaptureStepTypes.ID_CARD_BACK:
            return (content.idback.useCustomStartInstructionImage && content.idback.startInstructionsImageUrl)
                ? content.idback.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/idback.jpg`;
        case CaptureStepTypes.PASSPORT:
            return (content.passport.useCustomStartInstructionImage && content.passport.startInstructionsImageUrl)
                ? content.passport.startInstructionsImageUrl
                : `https://dcui-assets.s3.amazonaws.com/img/passport.jpg`;
        default:
            return "";
    }
};

export const getCaptureStepLabel = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return content.face.label;
        case CaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.label;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.label;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.label;
        case CaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.label;
        case CaptureStepTypes.ID_CARD_BACK:
            return content.idback.label;
        case CaptureStepTypes.PASSPORT:
            return content.passport.label;
        default:
            return "";
    }
};

export const getCaptureStepUrl = (captureStep: CaptureStep): { url: string; side?: string } => {
    switch (captureStep.type) {
        case CaptureStepTypes.FACE:
            return { url: '/face' };
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return { url: '/driver-license', side: DriverLicenseSide.FRONT };
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return { url: '/driver-license', side: DriverLicenseSide.BACK };
        case CaptureStepTypes.ID_CARD_FRONT:
            return { url: '/id-card', side: IDCardSide.FRONT };
        case CaptureStepTypes.ID_CARD_BACK:
            return { url: '/id-card', side: IDCardSide.BACK };
        case CaptureStepTypes.PASSPORT:
            return { url: '/passport' };
        case CaptureStepTypes.LIVENESS_CHECK:
            return { url: '/liveness' };
        default:
            return null
    }
};

export const getCaptureStepInstructions = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return content.face.instructionsHtml;
        case CaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.instructionsHtml;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.instructionsHtml;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.instructionsHtml;
        case CaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.instructionsHtml;
        case CaptureStepTypes.ID_CARD_BACK:
            return content.idback.instructionsHtml;
        case CaptureStepTypes.PASSPORT:
            return content.passport.instructionsHtml;
        default:
            return "";
    }
};

export const getCaptureStepShowScreenOrientationPreview = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return content.face.showScreenOrientationPreview;
        case CaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.showScreenOrientationPreview;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.showScreenOrientationPreview;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.showScreenOrientationPreview;
        case CaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.showScreenOrientationPreview;
        case CaptureStepTypes.ID_CARD_BACK:
            return content.idback.showScreenOrientationPreview;
        case CaptureStepTypes.PASSPORT:
            return content.passport.showScreenOrientationPreview;
        default:
            return false;
    }
};

export const getCaptureStepNativeCameraButtonText = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    let buttonText;
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            buttonText = content.face.nativeCameraButtonText;
            break;
        case CaptureStepTypes.LIVENESS_CHECK:
            buttonText = content.face3d.nativeCameraButtonText;
            break;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            buttonText = content.dlfront.nativeCameraButtonText;
            break;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            buttonText = content.dlback.nativeCameraButtonText;
            break;
        case CaptureStepTypes.ID_CARD_FRONT:
            buttonText = content.idfront.nativeCameraButtonText;
            break;
        case CaptureStepTypes.ID_CARD_BACK:
            buttonText = content.idback.nativeCameraButtonText;
            break;
        case CaptureStepTypes.PASSPORT:
            buttonText = content.passport.nativeCameraButtonText;
            break;
        default:
            buttonText = content.general.nativeCameraButtonText;
            break;
    }
    return buttonText || content.general.nativeCameraButtonText;
};

export const getCaptureStepScreenOrientationPreviewMessage = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return content.face.screenOrientationPreviewText;
        case CaptureStepTypes.LIVENESS_CHECK:
            return content.face3d.screenOrientationPreviewText;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return content.dlfront.screenOrientationPreviewText;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return content.dlback.screenOrientationPreviewText;
        case CaptureStepTypes.ID_CARD_FRONT:
            return content.idfront.screenOrientationPreviewText;
        case CaptureStepTypes.ID_CARD_BACK:
            return content.idback.screenOrientationPreviewText;
        case CaptureStepTypes.PASSPORT:
            return content.passport.screenOrientationPreviewText;
        default:
            return "";
    }
};

export const getCaptureStepScreenOrientationMediaUrl = (captureStepType: CaptureStepTypes, content: DCUIContent) => {
    switch (captureStepType) {
        case CaptureStepTypes.FACE:
            return (content.face.useCustomScreenOrientationVideo && content.face.screenOrientationMediaUrl)
                ? content.face.screenOrientationMediaUrl
                : content.face.screenOrientationVideoUrl;
        case CaptureStepTypes.LIVENESS_CHECK:
            return (content.face3d.useCustomScreenOrientationVideo && content.face3d.screenOrientationMediaUrl)
                ? content.face3d.screenOrientationMediaUrl
                : content.face3d.screenOrientationVideoUrl;
        case CaptureStepTypes.DRIVER_LICENSE_FRONT:
            return (content.dlfront.useCustomScreenOrientationVideo && content.dlfront.screenOrientationMediaUrl)
                ? content.dlfront.screenOrientationMediaUrl
                : content.dlfront.screenOrientationVideoUrl;
        case CaptureStepTypes.ID_CARD_FRONT:
            return (content.idfront.useCustomScreenOrientationVideo && content.idfront.screenOrientationMediaUrl)
                ? content.idfront.screenOrientationMediaUrl
                : content.idfront.screenOrientationVideoUrl;
        case CaptureStepTypes.DRIVER_LICENSE_BACK:
            return (content.dlback.useCustomScreenOrientationVideo && content.dlback.screenOrientationMediaUrl)
                ? content.dlback.screenOrientationMediaUrl
                : content.dlback.screenOrientationVideoUrl;
        case CaptureStepTypes.ID_CARD_BACK:
            return (content.idback.useCustomScreenOrientationVideo && content.idback.screenOrientationMediaUrl)
                ? content.idback.screenOrientationMediaUrl
                : content.idback.screenOrientationVideoUrl;
        case CaptureStepTypes.PASSPORT:
            return (content.passport.useCustomScreenOrientationVideo && content.passport.screenOrientationMediaUrl)
                ? content.passport.screenOrientationMediaUrl
                : content.passport.screenOrientationVideoUrl;
        default:
            return "";
    }
};

export const getNextCaptureStepUrl = (captureSteps: Array<CaptureStep>) => {
    let nextCaptureStep: { url: string; side?: string };
    const incompleteCaptureStep = captureSteps.find(captureStep => !captureStep.completed && !captureStep.optional && captureStep.method !== CaptureStepTypes.NONE);
    if (incompleteCaptureStep) {
        Logger.info(`Incomplete step found: ${incompleteCaptureStep.label}`);
        nextCaptureStep = getCaptureStepUrl(incompleteCaptureStep);
    } else {
        nextCaptureStep = { url: "/complete" };
    }

    let nextCaptureStepUrl;
    if (nextCaptureStep?.url) {
        nextCaptureStepUrl = `/dcui${nextCaptureStep.url}`;
        let log = `Next step url: ${nextCaptureStep.url}`;
        if (nextCaptureStep.side) {
            nextCaptureStepUrl += `/${nextCaptureStep.side}`;
            log += `, Next step side: ${nextCaptureStep.side}`;
        }
        Logger.info(log);
    }

    return nextCaptureStepUrl;
}